import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IModal } from '../../../../../../components/modals/modal.interface';
import { IModalDialogInputData } from '../../../../../../interfaces/modal-dialog.interface';
import { ModalContainerComponent } from '../../../../../../components/modals/modal-container.component';
import { ModalService } from '../../../../../../services/modal.service';
import { ManualAuditIssueService } from '../../../../../../services/manual-audit-issue.service';
import { $auditIssueFields, $formatColumns } from '../../../../../../../../shared/constants/manual-audit-issues';
import { ManualAuditQuery } from '../../../../../../../../shared/constants/audit-finding';
import { IManualAuditQuery } from '../../../../../../../../shared/interfaces/manual-audit-finding.interface';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';

interface ControlDef {
  name: $auditIssueFields;
  default: boolean;
  disabled: boolean;
  labelKey?: string;
}

const baseDescriptionFields: readonly ControlDef[] = Object.freeze([
  { name: $auditIssueFields.screen, default: false, disabled: false },
  { name: $auditIssueFields.url, default: false, disabled: false },
  { name: $auditIssueFields.issueFrequency, default: false, disabled: false },
  { name: $auditIssueFields.stepsToReproduce, default: false, disabled: false },
  { name: $auditIssueFields.actualResult, default: false, disabled: false },
  { name: $auditIssueFields.screenshot, default: false, disabled: false },
  { name: $auditIssueFields.criteria, default: false, disabled: false },
  { name: $auditIssueFields.evaluationStandard, default: false, disabled: false },
  { name: $auditIssueFields.effort, default: false, disabled: false },
  { name: $auditIssueFields.instances, default: false, disabled: false },
  { name: $auditIssueFields.disabilityAffected, default: false, disabled: false },
  { name: $auditIssueFields.recommendation, default: false, disabled: false },
  { name: $auditIssueFields.attachments, default: false, disabled: false },
  { name: $auditIssueFields.age, default: false, disabled: false },
  { name: $auditIssueFields.category, default: false, disabled: false },
  { name: $auditIssueFields.taskId, default: false, disabled: false },
]);

const newDescriptionFields: readonly ControlDef[] = Object.freeze([
  { name: $auditIssueFields.xpath, default: false, disabled: false, labelKey: 'issue_export_field_xpath' },
  { name: $auditIssueFields.codeSnippet, default: false, disabled: false, labelKey: 'issue_export_field_codeSnippet' },
]);

const allFieldNames: string[] = [
  $auditIssueFields.screen,
  $auditIssueFields.url,
  $auditIssueFields.issueFrequency,
  $auditIssueFields.stepsToReproduce,
  $auditIssueFields.actualResult,
  $auditIssueFields.screenshot,
  $auditIssueFields.successCriteria,
  $auditIssueFields.conformanceLevel,
  $auditIssueFields.criteria,
  $auditIssueFields.evaluationStandard,
  $auditIssueFields.effort,
  $auditIssueFields.instances,
  $auditIssueFields.disabilityAffected,
  $auditIssueFields.recommendation,
  $auditIssueFields.attachments,
  $auditIssueFields.age,
  $auditIssueFields.category,
  $auditIssueFields.taskId,
  $auditIssueFields.xpath,
  $auditIssueFields.codeSnippet,
];

@Component({
  selector: 'app-issue-report-field-selector',
  templateUrl: './issue-report-field-selector.component.html',
  styleUrls: ['./issue-report-fields-selector.component.scss'],
  standalone: false,
})
export class IssueReportFieldSelectorComponent implements IModal, OnInit {
  @Input() public modalInputData: IModalDialogInputData<{
    issueCount: number;
    scanId: string;
    filterQuery: IManualAuditQuery;
    newFieldsAvailable: boolean;
  }>;
  @ViewChild(ModalContainerComponent, { static: true })
  public container: ModalContainerComponent;
  public form: UntypedFormGroup;
  public $auditIssueFields: typeof $auditIssueFields;
  public $formatColumns: typeof $formatColumns;
  public ManualAuditQuery: typeof ManualAuditQuery;
  public currentSelectedCount: number;
  private descriptionKeys: ReadonlySet<$auditIssueFields>;
  private allDescriptionFields$: BehaviorSubject<ControlDef[]>;

  public firstColumnDescriptionFields$: Observable<ControlDef[]>;
  public secondColumnDescriptionFields$: Observable<ControlDef[]>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    private manualAuditIssueService: ManualAuditIssueService,
  ) {
    this.$auditIssueFields = $auditIssueFields;
    this.$formatColumns = $formatColumns;
    this.ManualAuditQuery = ManualAuditQuery;
    this.currentSelectedCount = 0;

    this.allDescriptionFields$ = new BehaviorSubject<ControlDef[]>([]);
  }

  private createForm(fields: ControlDef[]): void {
    this.form = this.formBuilder.group({});

    fields.forEach((field: ControlDef): void => {
      this.form.addControl(field.name, new UntypedFormControl({ value: field.default, disabled: field.disabled }));
    });
    this.form.addControl(ManualAuditQuery.formatColumns, new UntypedFormControl($formatColumns.singleDescriptionColumn));
  }

  public getSelectedFields(): string[] {
    const selectedFields: string[] = [];
    for (const field of allFieldNames) {
      if (this.form.get(field)?.value) {
        selectedFields.push(field);
      }
    }
    return selectedFields;
  }

  private getFormatColumns(): $formatColumns {
    return this.form.get(ManualAuditQuery.formatColumns).value;
  }

  public selectedChange(ctrlDef: ControlDef): void {
    if (this.form.get(ctrlDef.name).value) {
      this.currentSelectedCount += 1;
    } else {
      this.currentSelectedCount -= 1;
    }
  }

  public closeModal(): void {
    this.modalService.closeModal();
  }

  public onClose(): void {
    this.closeModal();
  }

  public selectAllColumns(): void {
    for (const descriptionKey of this.descriptionKeys) {
      const control: AbstractControl = this.form.get(descriptionKey);

      if (SharedCommonUtility.notNullish(control) && control.enabled && control.value === false) {
        control.setValue(true, { emitEvent: false });
        this.currentSelectedCount += 1;
      }
    }
  }

  public unselectAllColumns(): void {
    for (const descriptionKey of this.descriptionKeys) {
      const control: AbstractControl = this.form.get(descriptionKey);

      if (SharedCommonUtility.notNullish(control) && control.enabled && control.value === true) {
        control.setValue(false, { emitEvent: false });
        this.currentSelectedCount -= 1;
      }
    }
  }

  public async onExport(): Promise<void> {
    this.closeModal();

    await this.manualAuditIssueService
      .downloadCSVReport(
        this.modalInputData.data.scanId,
        this.getSelectedFields(),
        this.getFormatColumns(),
        this.modalInputData.data.filterQuery,
      )
      .toPromise();
  }

  public ngOnInit(): void {
    const descriptionFields: ControlDef[] = [
      ...baseDescriptionFields,
      ...(this.modalInputData.data.newFieldsAvailable ? newDescriptionFields : []),
    ];
    this.descriptionKeys = new Set(descriptionFields.map((field: ControlDef): $auditIssueFields => field.name));
    this.allDescriptionFields$.next(descriptionFields);
    this.createForm(descriptionFields);

    this.firstColumnDescriptionFields$ = this.allDescriptionFields$.pipe(
      map((allFields: ControlDef[]) => {
        return allFields.slice(0, Math.ceil(allFields.length / 2));
      }),
    );

    this.secondColumnDescriptionFields$ = this.allDescriptionFields$.pipe(
      map((allFields: ControlDef[]) => {
        return allFields.slice(Math.ceil(allFields.length / 2));
      }),
    );
  }
}
